/**
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TRACE_RESPONSE_SHAPE_FIX
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Align Monitoring page response handling with the admin tracing service payload.
 *
 * File Size Check:
 * - Current: 330 lines
 * - Adding: ~30 lines
 * - Projected: 360 lines
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: existing .h1, .h5, .text-danger, .badge, table classes
 * - Tokens used: --font-size-base
 *
 * Related Files:
 * - client/src/pages/admin/MonitoringPage.tsx
 * - client/src/services/adminTraces.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COLOR_COMPLIANCE_PHASE1_MONITORING
 * Date:         2025-01-20
 * Modified By:  Cursor
 *
 * Description:
 * Replaced hardcoded bar chart fill color with design token CSS variable
 * to ensure compliance with Figma design system.
 *
 * Related Screens / Modules:
 * - Admin Dashboard > Monitoring > Status Distribution Chart
 * - client/src/pages/admin/MonitoringPage.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TRACING_SYSTEM
 * Date:         2025-12-20
 * Modified By:  Cursor
 *
 * Description:
 * Admin monitoring dashboard for trace visualization and real-time monitoring.
 */

import React, { useState, useEffect, useCallback } from "react";
import { getAllTraces, getTraceStats, TraceStats } from "../../services/adminTraces";
import { Trace } from "../../services/traces";
import { Card, Table, Form, Button } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export function MonitoringPage() {
  const [traces, setTraces] = useState<Trace[]>([]);
  const [stats, setStats] = useState<TraceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    type: "",
    userName: "",
    subtype: "",
    page: 1,
    limit: 20,
  });

  // [2025-12-20] Tracing: Fetch stats function
  const fetchStats = useCallback(async () => {
    try {
      const res = await getTraceStats();
      const payload = res?.data;
      // [2026-01-07] MonitoringResponse: handle the direct JSON payload returned by adminTraces service.
      if (res?.success === false || payload == null) {
        setStats(null);
        return;
      }
      setStats(payload);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  // [2025-12-20] Tracing: Fetch traces function
  const fetchTraces = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllTraces(filters);
      const payload = res?.data;
      // [2026-01-07] MonitoringResponse: guard against missing payload and success=false flags.
      if (res?.success === false || payload == null) {
        setTraces([]);
        return;
      }
      setTraces(payload);
    } catch (error) {
      console.error("Failed to fetch traces:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // [2025-12-20] Tracing: Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // [2025-12-20] Tracing: Fetch traces when filters change
  useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  // [2025-12-20] Tracing: Auto-refresh every 5 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchTraces();
      fetchStats();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [fetchTraces, fetchStats]);

  // [2025-12-20] Tracing: Prepare chart data
  const chartData = stats
    ? [
        { name: "PENDING", value: stats.byStatus.PENDING || 0 },
        { name: "PROCESSING", value: stats.byStatus.PROCESSING || 0 },
        { name: "SUCCESS", value: stats.byStatus.SUCCESS || 0 },
        { name: "FAILED", value: stats.byStatus.FAILED || 0 },
      ]
    : [];

  return (
    <div className="container-fluid mt-4">
      <h1>System Monitoring</h1>

      {/* Statistics Cards */}
      {stats && (
        <div className="row mb-4">
          <div className="col-md-3">
            <Card>
              <Card.Body>
                <h5>Total Requests</h5>
                <h2>{stats.total}</h2>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-3">
            <Card>
              <Card.Body>
                <h5>Success Rate</h5>
                <h2>
                  {stats.byStatus.SUCCESS
                    ? Math.round((stats.byStatus.SUCCESS / stats.total) * 100)
                    : 0}
                  %
                </h2>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-3">
            <Card>
              <Card.Body>
                <h5>Avg Duration</h5>
                <h2>{Math.round(stats.avgDuration.avgMs)}ms</h2>
              </Card.Body>
            </Card>
          </div>
          <div className="col-md-3">
            <Card>
              <Card.Body>
                <h5>Failed</h5>
                <h2 className="text-danger">{stats.byStatus.FAILED || 0}</h2>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {/* Status Chart */}
      {stats && (
        <Card className="mb-4">
          <Card.Body>
            <h5>Status Distribution</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {/* [2025-01-20] Color compliance: Use design token for bar chart fill (changed from #0d6efd) */}
                <Bar dataKey="value" fill="var(--color-status-blue)" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <div className="row">
            <div className="col-md-3">
              <Form.Control
                placeholder="User name or email"
                value={filters.userName}
                onChange={(e) =>
                  setFilters({ ...filters, userName: e.target.value, page: 1 })
                }
              />
            </div>
            <div className="col-md-3">
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Select
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value, subtype: "", page: 1 })
                }
              >
                <option value="">All Types</option>
                <option value="MAIL">Mail</option>
                <option value="UPLOAD">Upload</option>
                <option value="BROADCAST">Broadcast</option>
                <option value="INVENTORY_BULK">Inventory Bulk</option>
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Select
                value={filters.subtype}
                onChange={(e) => setFilters({ ...filters, subtype: e.target.value, page: 1 })}
                disabled={!filters.type}
              >
                <option value="">All Subtypes</option>
                {filters.type === "MAIL" && (
                  <>
                    <option value="verification-email">Verification Email</option>
                    <option value="password-reset">Password Reset</option>
                    <option value="broadcast-notification">Broadcast Notification</option>
                  </>
                )}
                {filters.type === "UPLOAD" && (
                  <>
                    <option value="inventory-bulk">Inventory Bulk</option>
                    <option value="profile-image">Profile Image</option>
                  </>
                )}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Button variant="outline-secondary" onClick={fetchTraces}>
                Refresh
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Traces Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table hover>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Trace ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Device</th>
                  <th>Location</th>
                  <th>Progress</th>
                  <th>Duration</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {traces.map((trace: any) => (
                  <tr key={trace.traceId}>
                    <td>
                      {trace.userId ? (
                        typeof trace.userId === 'object' ? (
                          <>
                            <strong>
                              {`${trace.userId.firstName || ''} ${trace.userId.lastName || ''}`.trim() ||
                                trace.userId.email}
                            </strong>
                            <div>
                              <small className="text-muted">{trace.userId.email}</small>
                            </div>
                          </>
                        ) : (
                          <small>{trace.userId}</small>
                        )
                      ) : (
                        <small>-</small>
                      )}
                    </td>
                    <td>
                      <code>{trace.traceId}</code>
                    </td>
                    <td>
                      <span className="badge bg-secondary">{trace.operationType}</span>
                    </td>
                    <td>
                      <span className={`badge bg-${getStatusColor(trace.status)}`}>
                        {trace.status}
                      </span>
                    </td>
                    <td>{trace.description}</td>
                    <td>
                      {trace.deviceInfo && (
                        <small>
                          {trace.deviceInfo.device || "unknown"} •{" "}
                          {trace.deviceInfo.browser || "unknown"}
                        </small>
                      )}
                    </td>
                    <td>
                      {trace.locationInfo && (
                        <small>
                          {trace.locationInfo.city || "Unknown"},{" "}
                          {trace.locationInfo.country || "Unknown"}
                        </small>
                      )}
                    </td>
                    <td>
                      {trace.progress.total > 0 && (
                        <div className="progress" style={{ width: "100px" }}>
                          <div
                            className="progress-bar"
                            style={{ width: `${trace.progress.percentage}%` }}
                          >
                            {trace.progress.percentage}%
                          </div>
                        </div>
                      )}
                    </td>
                    <td>
                      {trace.timing.durationMs ? `${Math.round(trace.timing.durationMs)}ms` : "-"}
                    </td>
                    <td>{new Date(trace.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

function getStatusColor(status: string): string {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "FAILED":
      return "danger";
    case "PROCESSING":
      return "info";
    case "PENDING":
      return "warning";
    default:
      return "secondary";
  }
}
